import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/components/shared";
import { PersonPicker, TeamPicker } from "@/components/people/PersonPicker";
import { MILESTONE_PRESETS, fmtMoney } from "@/lib/constants";
import { StatusChangeDialog } from "./StatusChangeDialog";

export const LiveSetupDialog = ({ project: p, people, onPersonAdded, templates, currencies, onCancel, onConfirm }) => {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setF({ project_manager_id: p.project_manager_id || p.owner_id || "", team: (p.team || []).map((t) => ({ person_id: t.person_id, role: t.role || "" })), start_date: p.start_date || new Date().toISOString().slice(0, 10),
      target_delivery_date: p.target_delivery_date || "", fee_amount: p.fee_amount ?? "", fee_currency: p.fee_currency || "INR",
      template_id: "none", milestone_preset: "none" });
  }, [p]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const hasChecklist = (p.checklist || []).length > 0;
  const hasMilestones = (p.milestones || []).length > 0;
  const matching = templates.filter((t) => t.project_type === p.project_type);
  const others = templates.filter((t) => t.project_type !== p.project_type);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onConfirm({ setup: {
        project_manager_id: f.project_manager_id || null, team: f.team,
        start_date: f.start_date || null, target_delivery_date: f.target_delivery_date || null,
        fee_amount: f.fee_amount === "" ? null : Number(f.fee_amount), fee_currency: f.fee_currency,
        template_id: f.template_id === "none" ? null : f.template_id, milestone_preset: f.milestone_preset === "none" ? null : f.milestone_preset,
      } });
    } finally { setBusy(false); }
  };
  const missingDocs = (p.doc_checklist || []).filter((i) => i.state === "missing" && i.key !== "rfp").map((i) => i.label);
  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-xl" data-testid="live-setup-dialog">
        <DialogHeader>
          <DialogTitle>Activate project · {p.project_code}</DialogTitle>
          <DialogDescription>Confirm the execution setup before moving to Live / In Execution.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-1">
          {missingDocs.length > 0 && (
            <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="ls-missing-docs">
              <AlertTriangle className="h-3.5 w-3.5 mt-px shrink-0" />
              <span>Documents still pending: {missingDocs.join(", ")}. Add them in the Documents tab or mark them N/A — this does not block activation.</span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Project manager" required>
              <PersonPicker people={people} value={f.project_manager_id || null} onChange={set("project_manager_id")} onAdded={onPersonAdded} testid="ls-pm-select" />
            </Field>
            <Field label="Start date" required><Input data-testid="ls-start-input" type="date" value={f.start_date || ""} onChange={set("start_date")} required /></Field>
          </div>
          <Field label="Delivery team" hint="Optional project-specific role per person"><TeamPicker people={people} value={f.team || []} onChange={set("team")} onAdded={onPersonAdded} testid="ls-team" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Agreed project fee" required><Input data-testid="ls-fee-input" type="number" min="0" step="any" value={f.fee_amount ?? ""} onChange={set("fee_amount")} required /></Field>
            <Field label="Currency">
              <Select value={f.fee_currency || "INR"} onValueChange={set("fee_currency")}>
                <SelectTrigger data-testid="ls-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>{[...new Set([...(currencies || []), f.fee_currency || "INR"])].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-3">
            <div className="text-xs font-semibold text-slate-700">Execution plan</div>
            <div className="grid grid-cols-2 gap-4">
              <Field label={hasChecklist ? `Checklist (${p.checklist.length} tasks already)` : "Execution checklist"} hint={hasChecklist ? "Template tasks are added to existing ones" : "Pick a template or add tasks afterwards"}>
                <Select value={f.template_id || "none"} onValueChange={set("template_id")}>
                  <SelectTrigger data-testid="ls-template-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No template — add tasks later</SelectItem>
                    {matching.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.items.length} tasks)</SelectItem>)}
                    {others.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} · {t.project_type}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label={hasMilestones ? `Payment milestones (${p.milestones.length} already set)` : "Payment milestones"} hint={hasMilestones ? "Existing milestones are kept" : "Split of the agreed fee; editable later"}>
                <Select value={f.milestone_preset || "none"} onValueChange={set("milestone_preset")} disabled={hasMilestones}>
                  <SelectTrigger data-testid="ls-milestone-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{MILESTONE_PRESETS.map((m) => <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>
            {(!hasChecklist && f.template_id === "none") || (!hasMilestones && f.milestone_preset === "none") ? (
              <div className="flex items-start gap-2 text-xs text-amber-800" data-testid="ls-reminder">
                <AlertTriangle className="h-3.5 w-3.5 mt-px shrink-0" />
                <span>{[!hasChecklist && f.template_id === "none" && "checklist", !hasMilestones && f.milestone_preset === "none" && "payment milestones"].filter(Boolean).join(" and ")} not set — you will see a reminder on the project page until added.</span>
              </div>
            ) : null}
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onCancel} data-testid="ls-cancel-button">Cancel</Button>
            <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={busy || !f.project_manager_id} data-testid="ls-confirm-button">{busy ? "Activating…" : "Activate project"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const ExecutedDialog = ({ project: p, onCancel, onConfirm }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const items = p.checklist || [];
  const unfinished = items.filter((i) => i.status !== "completed");
  const fin = p.financials;
  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md" data-testid="executed-dialog">
        <DialogHeader>
          <DialogTitle>Mark as Executed · {p.project_code}</DialogTitle>
          <DialogDescription>Executed means delivery is complete. It does not mark invoices as paid — outstanding amounts stay visible in Payments.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {unfinished.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2" data-testid="executed-all-done"><CheckCircle2 className="h-4 w-4" /> All {items.length} checklist tasks are completed.</div>
          ) : (
            <div className="text-sm bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="executed-warning">
              <div className="flex items-center gap-2 font-medium text-amber-800"><AlertTriangle className="h-4 w-4" /> {unfinished.length} of {items.length} checklist tasks are not completed</div>
              <ul className="mt-1.5 ml-6 list-disc text-xs text-amber-900 space-y-0.5">{unfinished.slice(0, 5).map((i) => <li key={i.id}>{i.title}</li>)}{unfinished.length > 5 && <li>…and {unfinished.length - 5} more</li>}</ul>
              <Field label="Override reason" required>
                <Textarea data-testid="executed-override-input" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Why is delivery complete despite open tasks?" className="mt-1 bg-white" />
              </Field>
            </div>
          )}
          {fin && fin.status !== "paid" && (
            <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded px-3 py-2" data-testid="executed-financial-note">
              Financial status: <span className="font-medium">{fin.status === "not_invoiced" ? "nothing invoiced yet" : `${fmtMoney(fin.outstanding, fin.currency)} outstanding`}</span>. This stays tracked under Payments.
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onCancel} data-testid="executed-cancel-button">Cancel</Button>
            <Button data-testid="executed-confirm-button" className="bg-teal-600 hover:bg-teal-700" disabled={busy || (unfinished.length > 0 && !reason.trim())}
              onClick={async () => { setBusy(true); try { await onConfirm(unfinished.length ? { override_reason: reason.trim() } : {}); } finally { setBusy(false); } }}>
              {unfinished.length ? "Override and mark Executed" : "Mark Executed"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const NEEDS_DIALOG = ["lost_cancelled", "live", "executed", "proposal_sent", "proposal_accepted"];

const REMINDERS = {
  proposal_sent: { title: "Moving to Proposal / Estimate Sent", items: (p) => [["sow", "Attach the SOW that was sent"], ["estimate", "Attach the estimate / commercial proposal that was sent"]] },
  proposal_accepted: { title: "Moving to Approved", items: (p) => [["estimate", "Mark the accepted estimate version with its acceptance date"], ["fee", "Confirm the agreed fee in Commercials & Payments", p.fee_type === "agreed"]] },
};

export const ReminderDialog = ({ project: p, status, onCancel, onConfirm }) => {
  const [busy, setBusy] = useState(false);
  const r = REMINDERS[status];
  const check = Object.fromEntries((p.doc_checklist || []).map((i) => [i.key, i]));
  const rows = r.items(p).map(([k, label, done]) => ({ k, label, ok: k === "fee" ? done : check[k]?.state === "added" || check[k]?.state === "not_required", nr: check[k]?.state === "not_required" }));
  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md" data-testid="reminder-dialog">
        <DialogHeader><DialogTitle>{r.title} · {p.project_code}</DialogTitle><DialogDescription>Helpful reminders — nothing here blocks the stage change.</DialogDescription></DialogHeader>
        <ul className="space-y-2">
          {rows.map((x) => <li key={x.k} className="flex items-start gap-2 text-sm" data-testid={`reminder-${x.k}`}>{x.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /> : <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />}<span className={x.ok ? "text-slate-500" : "text-slate-800"}>{x.label}{x.nr ? " (marked not required)" : ""}</span></li>)}
        </ul>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onCancel} data-testid="reminder-cancel-button">Cancel</Button>
          <Button className="bg-teal-600 hover:bg-teal-700" disabled={busy} onClick={async () => { setBusy(true); try { await onConfirm({}); } finally { setBusy(false); } }} data-testid="reminder-confirm-button">Continue</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const StageDialogs = ({ pending, people, onPersonAdded, templates, currencies, onCancel, onConfirm }) => {
  if (!pending) return null;
  if (REMINDERS[pending.status]) return <ReminderDialog project={pending.project} status={pending.status} onCancel={onCancel} onConfirm={onConfirm} />;
  if (pending.status === "lost_cancelled") return <StatusChangeDialog pending={pending} onCancel={onCancel} onConfirm={onConfirm} />;
  if (pending.status === "live") return <LiveSetupDialog project={pending.project} people={people} onPersonAdded={onPersonAdded} templates={templates} currencies={currencies} onCancel={onCancel} onConfirm={onConfirm} />;
  if (pending.status === "executed") return <ExecutedDialog project={pending.project} onCancel={onCancel} onConfirm={onConfirm} />;
  return null;
};
